import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import {
  FlightCheckbox,
  FlightSelect,
  FlightTable,
  FlightTextInput,
  FlightTooltip,
  getIcon,
} from '@flybits/design-system';
import { formatBytes } from 'helpers/common.helper';
import useDebounce from 'hooks/useDebounce';
import useCtxDataFiles, { formatCompletedTimestamp } from 'hooks/useCtxDataFiles';
import { useAppSelector as useSelector } from 'hooks/reduxHooks';
import { JOURNEY_STATUSES } from 'store/journey/journey.type';
import { CtxDataFile } from 'interface/file/file.interface';
import { FILE_STATUS } from 'pages/ExperienceCanvas/types';
import LoadingIcon from 'components/Shared/LoadingIcon/LoadingIcon';
import QueueRingIcon from '../assets/QueueRingIcon';
import RefreshIcon from '../assets/RefreshIcon';
import PauseIcon from '../assets/PauseIcon';
import PlayIcon from '../assets/PlayIcon';
import useFileActionRunner from '../useFileActionRunner';
import { PROCESSING_QUEUED, STATUS_LABELS, STATUS_PRIORITY, StatusGroup, classifyStatus } from '../fileStatusGrouping';
import './FlowFileUploadHistory.scss';

const MAIN_CLASS = 'flow-file-upload-history';

const CLASSES = {
  HEADER: `${MAIN_CLASS}__header`,
  HEADER_TITLE: `${MAIN_CLASS}__header-title`,
  HEADER_CONTROLS: `${MAIN_CLASS}__header-controls`,
  ACTION_BTN: `${MAIN_CLASS}__action-btn`,
  ACTION_BTN_PRIMARY: `${MAIN_CLASS}__action-btn--primary`,
  ACTION_BTN_PAUSE: `${MAIN_CLASS}__action-btn--pause`,
  BANNER: `${MAIN_CLASS}__banner`,
  TABLE_WRAPPER: `${MAIN_CLASS}__table-wrapper`,
  STATUS_CELL: `${MAIN_CLASS}__status-cell`,
  STATUS_CELL_ERROR: `${MAIN_CLASS}__status-cell--error`,
  ERROR_TOGGLE: `${MAIN_CLASS}__error-toggle`,
  ERROR_CONTAINER: `${MAIN_CLASS}__error-container`,
  FILENAME_CELL: `${MAIN_CLASS}__filename-cell`,
  EMPTY_STATE: `${MAIN_CLASS}__empty-state`,
  FOOTER_BAR: `${MAIN_CLASS}__footer-bar`,
  FOOTER_TIMER: `${MAIN_CLASS}__footer-timer`,
  FOOTER_REFRESH: `${MAIN_CLASS}__footer-refresh`,
  FOOTER_COMPLETED: `${MAIN_CLASS}__footer-completed`,
};

const SORTABLE_HEADER_KEYS = new Set(['fileName', 'size', 'uploadedOn', 'status']);

const STATUS_FILTER_OPTIONS = [
  { key: '', name: 'All statuses' },
  { key: FILE_STATUS.PROCESSING, name: 'Processing' },
  { key: FILE_STATUS.PROCESSING_PAUSED, name: 'Paused' },
  { key: FILE_STATUS.PROCESSING_FAILED, name: 'Failed' },
  { key: FILE_STATUS.UPLOADED, name: 'Ready' },
  { key: FILE_STATUS.PROCESSING_SUCCESS, name: 'Processed' },
];

const FlowFileUploadHistory: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [activeStatusFilter, setActiveStatusFilter] = useState<string>('');
  const [sortByHeaderKey, setSortByHeaderKey] = useState<string>('uploadedOn');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set());
  const [secondsAgo, setSecondsAgo] = useState(0);
  const lastFetchTimeRef = useRef<number>(Date.now());

  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const journeyStatus = useSelector((state) => state.te.journey.status);
  const isJourneyActive = journeyStatus === JOURNEY_STATUSES.ACTIVE;
  const { localFileStatuses, localFileErrors, isRunning, inFlightFileIds, setInFlightFileIds, runByIds } =
    useFileActionRunner();

  // Pull the journey-wide file list in a single request and filter/sort/page
  // entirely on the client. This avoids per-status count requests and the
  // "page 1 hides processed files" issue caused by server-side sorting.
  const {
    isFetching,
    files: allFiles,
    counts,
    dataUpdatedAt,
    refetch,
  } = useCtxDataFiles({
    limit: 1000,
    sortby: 'createdAt',
    sortorder: 'desc',
  });

  const getEffectiveStatus = useCallback(
    (file: CtxDataFile): string => {
      // Server-terminal statuses (success/failed) win over any stale optimistic
      // overlay so previously-completed files don't get pulled back into the
      // paused/processing buckets during pause/resume actions.
      if (file.status === FILE_STATUS.PROCESSING_SUCCESS || file.status === FILE_STATUS.PROCESSING_FAILED) {
        return file.status;
      }
      return localFileStatuses[file.id] ?? file.status;
    },
    [localFileStatuses],
  );

  const readyCount = useMemo(
    () => allFiles.filter((f) => getEffectiveStatus(f) === FILE_STATUS.UPLOADED).length,
    [allFiles, getEffectiveStatus],
  );

  // Status pill → list of effective statuses that count as a match. The
  // processing pill bundles PROCESSING + PROCESSING_QUEUED so the user doesn't
  // have to think about the queued sub-state.
  const statusFilterMatches = useCallback(
    (status: string): boolean => {
      if (!activeStatusFilter) return true;
      if (activeStatusFilter === FILE_STATUS.PROCESSING) {
        return status === FILE_STATUS.PROCESSING || status === PROCESSING_QUEUED;
      }
      if (activeStatusFilter === FILE_STATUS.PROCESSING_FAILED) {
        return status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED;
      }
      return status === activeStatusFilter;
    },
    [activeStatusFilter],
  );

  const filteredFiles = useMemo(() => {
    const needle = debouncedSearchTerm.trim().toLowerCase();
    return allFiles.filter((f) => {
      if (needle && !f.name.toLowerCase().includes(needle)) return false;
      if (!statusFilterMatches(getEffectiveStatus(f))) return false;
      return true;
    });
  }, [allFiles, debouncedSearchTerm, statusFilterMatches, getEffectiveStatus]);

  const sortedFiles = useMemo(() => {
    if (!SORTABLE_HEADER_KEYS.has(sortByHeaderKey)) return filteredFiles;
    const direction = sortOrder === 'asc' ? 1 : -1;
    const getValue = (file: CtxDataFile): string | number => {
      switch (sortByHeaderKey) {
        case 'fileName':
          return file.name.toLowerCase();
        case 'size':
          return file.size;
        case 'uploadedOn':
          return file.createdAt;
        case 'status':
          return getEffectiveStatus(file).toLowerCase();
        default:
          return 0;
      }
    };

    // Default sort path: priority group then uploadedOn desc.
    if (sortByHeaderKey === 'uploadedOn' && sortOrder === 'desc') {
      return [...filteredFiles].sort((a, b) => {
        const pa = STATUS_PRIORITY[getEffectiveStatus(a)] ?? 99;
        const pb = STATUS_PRIORITY[getEffectiveStatus(b)] ?? 99;
        if (pa !== pb) return pa - pb;
        return b.createdAt - a.createdAt;
      });
    }

    return [...filteredFiles].sort((a, b) => {
      const aVal = getValue(a);
      const bVal = getValue(b);
      if (aVal < bVal) return -1 * direction;
      if (aVal > bVal) return 1 * direction;
      return 0;
    });
  }, [filteredFiles, sortByHeaderKey, sortOrder, getEffectiveStatus]);

  const totalRecords = sortedFiles.length;
  const pageStart = (currentPage - 1) * perPage;
  const pagedFiles = useMemo(
    () => sortedFiles.slice(pageStart, pageStart + perPage),
    [sortedFiles, pageStart, perPage],
  );

  const handleHeaderSort = (header: { key: string }) => {
    if (!SORTABLE_HEADER_KEYS.has(header.key)) return;
    if (header.key !== sortByHeaderKey) {
      setSortByHeaderKey(header.key);
      setSortOrder('desc');
    } else {
      setSortOrder((order) => (order === 'desc' ? 'asc' : 'desc'));
    }
  };

  useEffect(() => {
    if (dataUpdatedAt) {
      lastFetchTimeRef.current = dataUpdatedAt;
      setSecondsAgo(0);
    }
  }, [dataUpdatedAt]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, perPage, activeStatusFilter]);

  // Clear the in-flight spinner once the server-truth status moves out of the
  // processing/queued bucket — i.e., the BE has actually advanced the file.
  useEffect(() => {
    setInFlightFileIds((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      allFiles.forEach((f) => {
        if (!next.has(f.id)) return;
        if (f.status !== FILE_STATUS.PROCESSING && f.status !== FILE_STATUS.PROCESSING_QUEUED) {
          next.delete(f.id);
        }
      });
      return next.size === prev.size ? prev : next;
    });
  }, [allFiles, setInFlightFileIds]);

  // Drop selections that are no longer on the current page so the action-bar
  // counts always reflect what the user can see.
  useEffect(() => {
    setSelectedFileIds((prev) => {
      if (prev.size === 0) return prev;
      const visibleIds = new Set(pagedFiles.map((f) => f.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (visibleIds.has(id)) next.add(id);
      });
      return next.size === prev.size ? prev : next;
    });
  }, [pagedFiles]);

  // Snap back to the last valid page if the active filter shrinks the result
  // set below the current page boundary.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(totalRecords / perPage));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [totalRecords, perPage, currentPage]);

  const completedTimestamp = useMemo(() => {
    if (!counts.allCompleted || !allFiles.length) return '';
    const latestCompletedAt = Math.max(...allFiles.map((f) => f.completedAt || 0));
    return latestCompletedAt > 0 ? formatCompletedTimestamp(latestCompletedAt) : '';
  }, [counts.allCompleted, allFiles]);

  // Group actions (pause-all / resume-all / process-all) target every file in
  // the journey for that status — not just the current page — so the action
  // buttons match the pill counts the user sees.
  const filesByGroup = useMemo(() => {
    const byGroup: Record<StatusGroup, CtxDataFile[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    allFiles.forEach((f) => {
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) byGroup[g].push(f);
    });
    return byGroup;
  }, [allFiles, getEffectiveStatus]);

  const selectedByGroup = useMemo(() => {
    const groups: Record<StatusGroup, string[]> = {
      processing: [],
      paused: [],
      ready: [],
      failed: [],
    };
    allFiles.forEach((f) => {
      if (!selectedFileIds.has(f.id)) return;
      const g = classifyStatus(getEffectiveStatus(f));
      if (g) groups[g].push(f.id);
    });
    return groups;
  }, [allFiles, selectedFileIds, getEffectiveStatus]);

  const showProcessingUi = counts.hasProcessing || isRunning;

  useEffect(() => {
    if (!allFiles.length) return;
    const interval = setInterval(() => {
      setSecondsAgo(Math.floor((Date.now() - lastFetchTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [allFiles.length]);

  const toggleFileSelection = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) next.delete(fileId);
      else next.add(fileId);
      return next;
    });
  };

  const dispatchAction = async (group: StatusGroup, fileIds: string[]) => {
    if (fileIds.length === 0) return;
    if (group === 'processing') {
      await runByIds({ action: 'PAUSE', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_PAUSED });
    } else if (group === 'paused') {
      await runByIds({ action: 'RESUME', fileIds, optimisticStatus: FILE_STATUS.PROCESSING_QUEUED });
    } else {
      // ready or failed (retry) → PROCESS
      await runByIds({ action: 'PROCESS', fileIds, optimisticStatus: FILE_STATUS.PROCESSING });
    }
    setSelectedFileIds((prev) => {
      const next = new Set(prev);
      fileIds.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handlePauseAll = (ids?: string[]) =>
    dispatchAction('processing', ids ?? filesByGroup.processing.map((f) => f.id));
  const handleResumeAll = (ids?: string[]) => dispatchAction('paused', ids ?? filesByGroup.paused.map((f) => f.id));
  const handleProcessAll = (ids?: string[]) => dispatchAction('ready', ids ?? filesByGroup.ready.map((f) => f.id));

  const renderFileNameCell = (file: CtxDataFile) => {
    const group = classifyStatus(getEffectiveStatus(file));
    const isInFlight = inFlightFileIds.has(file.id);
    const checkboxAllowed =
      isJourneyActive && (group === 'ready' || group === 'failed' || group === 'paused' || group === 'processing');

    if (checkboxAllowed) {
      return (
        <div className={CLASSES.FILENAME_CELL}>
          <FlightCheckbox
            checkState={selectedFileIds.has(file.id) ? 'SELECTED' : 'UNSELECTED'}
            onSelect={() => {
              if (isInFlight) return;
              toggleFileSelection(file.id);
            }}
            disabled={isInFlight}
          />
          <span>{file.name}</span>
        </div>
      );
    }
    return <div className={CLASSES.FILENAME_CELL}>{file.name}</div>;
  };

  const renderStatusCell = (file: CtxDataFile) => {
    const status = getEffectiveStatus(file);
    const label = STATUS_LABELS[status];
    const localError = localFileErrors[file.id];
    const isInFlight = inFlightFileIds.has(file.id);

    if (isInFlight) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label ?? 'Processing'}>
          <LoadingIcon />
        </div>
      );
    }

    if (localError) {
      return (
        <div className={`${CLASSES.STATUS_CELL} ${CLASSES.STATUS_CELL_ERROR}`}>
          <FlightTooltip description={localError} direction="top">
            {getIcon('error', { fill: '#dc3616' })}
          </FlightTooltip>
        </div>
      );
    }

    if (status === FILE_STATUS.PROCESSING_PAUSED) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <PauseIcon ariaHidden={false} />
        </div>
      );
    }
    if (status === FILE_STATUS.PROCESSING_SUCCESS) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          {getIcon('checkCircle', { fill: '#006117' })}
        </div>
      );
    }
    if (status === FILE_STATUS.PROCESSING || status === FILE_STATUS.UPLOADING) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <LoadingIcon />
        </div>
      );
    }
    if (status === PROCESSING_QUEUED || status === FILE_STATUS.UPLOADED) {
      return (
        <div className={CLASSES.STATUS_CELL} role="img" aria-label={label}>
          <QueueRingIcon ariaHidden={false} />
        </div>
      );
    }
    if (status === FILE_STATUS.PROCESSING_FAILED || status === FILE_STATUS.UPLOADING_FAILED) {
      const checkboxId = `flow-file-error-toggle-${file.id}`;
      return (
        <div className={`${CLASSES.STATUS_CELL} ${CLASSES.STATUS_CELL_ERROR}`}>
          {getIcon('error', { fill: '#dc3616' })}
          <input type="checkbox" id={checkboxId} className={CLASSES.ERROR_TOGGLE} />
          <label htmlFor={checkboxId} aria-label="Toggle error details">
            {getIcon('baselineKeyboardArrowDown', {})}
          </label>
          <div className={CLASSES.ERROR_CONTAINER}>
            <p>
              {getIcon('infoFilled', {})}
              <span>
                {label} - {moment(file.updatedAt * 1000).fromNow()}
              </span>
            </p>
            <p>{file.statusMessage}</p>
          </div>
        </div>
      );
    }
    return null;
  };

  const buildRow = (file: CtxDataFile) => ({
    key: file.fileID,
    fileName: renderFileNameCell(file),
    size: <div>{formatBytes(file.size)}</div>,
    uploadedOn: (
      <div title={moment(file.createdAt * 1000).format('MMMM, DD, YYYY, hh:mm:ss A')}>
        {moment(file.createdAt * 1000).format('MMM D,YYYY, h:mm:ssa')}
      </div>
    ),
    status: renderStatusCell(file),
  });

  const tableHeaders = [
    { name: 'File Name', key: 'fileName', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Size', key: 'size', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Uploaded on', key: 'uploadedOn', isVisible: true, isSortable: true, hideTooltip: true },
    { name: 'Status', key: 'status', isVisible: true, isSortable: true, hideTooltip: true },
  ];

  const paginationProps =
    totalRecords > perPage
      ? {
          totalPageNumber: Math.ceil(totalRecords / perPage),
          currentPageNumber: currentPage,
          currentRowsPerPage: perPage,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handlePageChange: (page: any) => setCurrentPage(page),
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          handleRowsPerPageChange: (rows: any) => setPerPage(rows),
        }
      : undefined;

  const renderHeaderAction = () => {
    if (!isJourneyActive) return null;

    const { processing: selProc, paused: selPaused, ready: selReady, failed: selFailed } = selectedByGroup;
    const hasSelection = selectedFileIds.size > 0;

    if (hasSelection) {
      if (selProc.length > 0) {
        return (
          <FlightTooltip description="Pause selected files" direction="bottom">
            <button
              type="button"
              className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PAUSE}`}
              onClick={() => handlePauseAll(selProc)}
              disabled={isRunning}
            >
              <span>Pause selected ({selProc.length})</span>
              <PauseIcon ariaHidden />
            </button>
          </FlightTooltip>
        );
      }
      if (selPaused.length > 0) {
        return (
          <button
            type="button"
            className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PRIMARY}`}
            onClick={() => handleResumeAll(selPaused)}
            disabled={isRunning}
          >
            <span>Resume selected ({selPaused.length})</span>
            <PlayIcon ariaHidden />
          </button>
        );
      }
      if (selReady.length > 0) {
        return (
          <button
            type="button"
            className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PRIMARY}`}
            onClick={() => handleProcessAll(selReady)}
            disabled={isRunning}
          >
            <span>Process selected ({selReady.length})</span>
            <PlayIcon ariaHidden />
          </button>
        );
      }
      if (selFailed.length > 0) {
        return (
          <button
            type="button"
            className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PRIMARY}`}
            onClick={() => dispatchAction('failed', selFailed)}
            disabled={isRunning}
          >
            <span>Retry selected ({selFailed.length})</span>
            <PlayIcon ariaHidden />
          </button>
        );
      }
      return null;
    }

    if (showProcessingUi) {
      return (
        <FlightTooltip description="Pause processing" direction="bottom">
          <button
            type="button"
            className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PAUSE}`}
            onClick={() => handlePauseAll()}
            disabled={isRunning}
          >
            <span>Pause processing</span>
            <PauseIcon ariaHidden />
          </button>
        </FlightTooltip>
      );
    }
    if (filesByGroup.paused.length > 0) {
      return (
        <button
          type="button"
          className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PRIMARY}`}
          onClick={() => handleResumeAll()}
          disabled={isRunning}
        >
          <span>Resume processing</span>
          <PlayIcon ariaHidden />
        </button>
      );
    }
    if (filesByGroup.ready.length > 0) {
      return (
        <button
          type="button"
          className={`${CLASSES.ACTION_BTN} ${CLASSES.ACTION_BTN_PRIMARY}`}
          onClick={() => handleProcessAll()}
          disabled={isRunning}
        >
          <span>Process files ({readyCount})</span>
          <PlayIcon ariaHidden />
        </button>
      );
    }
    return null;
  };

  return (
    <div className={MAIN_CLASS}>
      <div className={CLASSES.HEADER}>
        <h2 className={CLASSES.HEADER_TITLE}>Upload History</h2>
        <div className={CLASSES.HEADER_CONTROLS}>
          <FlightTextInput
            label="Search by name"
            iconInput="search"
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          />
          <FlightSelect
            options={STATUS_FILTER_OPTIONS}
            label="Status"
            selected={STATUS_FILTER_OPTIONS.find((o) => o.key === activeStatusFilter) || STATUS_FILTER_OPTIONS[0]}
            handleOptionClick={(option: { key: string; name: string }) => setActiveStatusFilter(option.key)}
          />
        </div>
        {renderHeaderAction()}
      </div>

      {showProcessingUi && (
        <div className={CLASSES.BANNER}>
          {getIcon('infoFilled', {})}
          <div>
            <strong>Processing one by one</strong>
            You&rsquo;re all set! Your files are queued and will start automatically. You can pause or resume at any
            time.
          </div>
        </div>
      )}

      <div className={CLASSES.TABLE_WRAPPER}>
        {sortedFiles.length === 0 && !isFetching ? (
          <div className={CLASSES.EMPTY_STATE}>No files to show</div>
        ) : (
          <FlightTable
            tableHeaders={tableHeaders}
            tableData={pagedFiles.map(buildRow)}
            paginationProps={paginationProps}
            hasPaginationBeforeTable={false}
            hasPaginationAfterTable={totalRecords > perPage}
            handleHeaderSort={handleHeaderSort}
            sortByKey={sortByHeaderKey}
            sortOrder={sortOrder}
          />
        )}
      </div>

      {isJourneyActive && allFiles.length > 0 && (
        <div className={CLASSES.FOOTER_BAR}>
          {counts.allCompleted && completedTimestamp ? (
            <span className={CLASSES.FOOTER_COMPLETED}>
              Completed: <span>{completedTimestamp}</span>
            </span>
          ) : (
            <span className={CLASSES.FOOTER_TIMER}>
              <strong>Last updated:</strong> {secondsAgo} second{secondsAgo !== 1 ? 's' : ''} ago
            </span>
          )}
          <button
            type="button"
            className={CLASSES.FOOTER_REFRESH}
            onClick={() => refetch()}
            aria-label="Refresh now"
            disabled={isFetching}
          >
            Refresh now <RefreshIcon ariaHidden />
          </button>
        </div>
      )}
    </div>
  );
};

export default FlowFileUploadHistory;
